import React from 'react';
import styled from 'styled-components';
import { LauncherWindowData, useLauncherWindowStore } from '../../hooks/stores/launcherWindowStore';
import { UIData, useUIStore } from '../../hooks/stores/uiStore';

const Container = styled.div`
  pointer-events: none;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  z-index: 9999;
`;

const SnapLine = styled.div<{ 
  $isHorizontal?: boolean; 
  $type?: 'center' | 'edge'; 
  $isWindow?: boolean;
  $isSameLevel?: boolean;
}>`
  position: absolute;
  z-index: 10000;
  opacity: ${props => props.$isSameLevel !== false ? 1 : 0.5};
  
  ${({ $type, $isHorizontal = true, $isWindow }) => {
    const color = $isWindow ? '#FF9800' : 'var(--primary-1)';
    
    if ($type === 'center') {
      return `
        ${$isHorizontal ? `
          width: 100%;
          height: 1px;
          background: repeating-linear-gradient(
            90deg,
            ${color},
            ${color} 4px,
            transparent 4px,
            transparent 8px
          );
        ` : `
          width: 1px;
          height: 100%;
          background: repeating-linear-gradient(
            0deg,
            ${color},
            ${color} 4px,
            transparent 4px,
            transparent 8px
          );
        `}
      `;
    }
    
    return `
      ${$isHorizontal ? `
        width: 100%;
        height: 1px;
      ` : `
        width: 1px;
        height: 100%;
      `}
      background-color: ${color};
      box-shadow: 0 0 2px ${color};
    `;
  }}
`;

const Corner = styled.div<{ 
  $isCenter?: boolean; 
  $isWindow?: boolean;
  $isSameLevel?: boolean;
}>`
  position: absolute;
  width: 4px;
  height: 4px;
  transform: translate(-50%, -50%);
  border-radius: ${props => props.$isCenter ? '0' : '50%'};
  background-color: ${props => props.$isWindow ? '#FF9800' : 'var(--primary-1)'};
  box-shadow: 0 0 2px ${props => props.$isWindow ? '#FF9800' : 'var(--primary-1)'};
  z-index: 10002;
  opacity: ${props => props.$isSameLevel !== false ? 1 : 0.2};
`;

export const SnappingGuideLines = React.memo(() => {
  const [UIData] = useUIStore<UIData>();
  const [launcherWindowData] = useLauncherWindowStore<LauncherWindowData>();


  const createGuide = (
    left: number,
    top: number,
    width: number | string,
    height: number | string,
    isWindow: boolean,
    type: 'center' | 'edge' = 'edge',
    isSameLevel: boolean = true,
    uniqueId: string,
    corners?: Array<{x: number, y: number}>
  ) => {
    const key = `guide-${uniqueId}-${type}-${left}-${top}-${width}-${height}`;
    return (
      <React.Fragment key={key}>
        <SnapLine
          $type={type}
          $isHorizontal={height === 1}
          $isWindow={isWindow}
          $isSameLevel={isSameLevel}
          style={{ left, top, width, height }}
        />
        {corners?.map((pos, i) => (
          <Corner
            key={`${key}-corner-${i}`}
            $isCenter={type === 'center'}
            $isWindow={isWindow}
            $isSameLevel={isSameLevel}
            style={{ 
              left: typeof width === 'number' && width === 1 ? left : pos.x,
              top: typeof height === 'number' && height === 1 ? top : pos.y
            }}
          />
        ))}
      </React.Fragment>
    );
  };

  const guides = React.useMemo(() => {
    if (!UIData.elementHandle.isVisible || !UIData.snapToEdge) return null;

    const guides: React.ReactNode[] = [];
    const { pos: handle, width: handleWidth, height: handleHeight } = UIData.elementHandle;
    const zoom = UIData.sceneZoom;

    // Get window dimensions
    const windowWidth = launcherWindowData.windowWidth;
    const windowHeight = launcherWindowData.windowHeight;

    UIData.snappedEdges.forEach((snap, index) => {
      // Check if handle is too far from window
      const isHandleVisible = (
        handle.x > -windowWidth && 
        handle.x < windowWidth * 2 && 
        handle.y > -windowHeight && 
        handle.y < windowHeight * 2
      );

      if (!isHandleVisible) return;

      if (!snap.elementID) {
        // Window guides
        switch (snap.handleEdge) {
          case 'centerX': {
            const x = launcherWindowData.windowWidth / 2;
            guides.push(createGuide(
              x,
              0,
              1,
              '100%',
              true,
              'center',
              true,
              `window-centerX-${index}`,
              [
                { x, y: 0 },
                { x, y: launcherWindowData.windowHeight },
                { x, y: handle.y },
                { x, y: handle.y + handleHeight }
              ]
            ));
            break;
          }
          case 'centerY': {
            const y = launcherWindowData.windowHeight / 2;
            guides.push(createGuide(
              0,
              y,
              '100%',
              1,
              true,
              'center',
              true,
              `window-centerY-${index}`,
              [
                { x: 0, y },
                { x: launcherWindowData.windowWidth, y },
                { x: handle.x, y },
                { x: handle.x + handleWidth, y }
              ]
            ));
            break;
          }
          case 'left': {
            guides.push(createGuide(
              0,
              0,
              1,
              '100%',
              true,
              'edge',
              true,
              `window-left-${index}`,
              [
                { x: 0, y: handle.y },
                { x: 0, y: handle.y + handleHeight }
              ]
            ));
            break;
          }
          case 'right': {
            const x = launcherWindowData.windowWidth;
            guides.push(createGuide(
              x,
              0,
              1,
              '100%',
              true,
              'edge',
              true,
              `window-right-${index}`,
              [
                { x, y: handle.y },
                { x, y: handle.y + handleHeight }
              ]
            ));
            break;
          }
          case 'top': {
            guides.push(createGuide(
              0,
              0,
              '100%',
              1,
              true,
              'edge',
              true,
              `window-top-${index}`,
              [
                { x: handle.x, y: 0 },
                { x: handle.x + handleWidth, y: 0 }
              ]
            ));
            break;
          }
          case 'bottom': {
            const y = launcherWindowData.windowHeight;
            guides.push(createGuide(
              0,
              y,
              '100%',
              1,
              true,
              'edge',
              true,
              `window-bottom-${index}`,
              [
                { x: handle.x, y },
                { x: handle.x + handleWidth, y }
              ]
            ));
            break;
          }
        }
      } else {
        // Element guides
        const element = document.getElementById(snap.elementID);
        const elementRect = element?.getBoundingClientRect();
        if (!elementRect) return;

        const windowDiv = document.getElementById('launcherWindow');
        const windowRect = windowDiv?.getBoundingClientRect();
        if (!windowRect) return;

        const elementPos = {
          x: (elementRect.left - windowRect.left) / zoom,
          y: (elementRect.top - windowRect.top) / zoom,
          width: elementRect.width / zoom,
          height: elementRect.height / zoom
        };

        // Check if element is too far from window
        const isElementVisible = (
          elementPos.x > -windowWidth && 
          elementPos.x < windowWidth * 2 && 
          elementPos.y > -windowHeight && 
          elementPos.y < windowHeight * 2
        );

        if (!isElementVisible) return;

        // Element edge guides
        switch (snap.handleEdge) {
          case 'centerX': {
            const centerX = elementPos.x + elementPos.width / 2;
            guides.push(createGuide(
              centerX,
              Math.min(elementPos.y, handle.y),
              1,
              Math.max(elementPos.y + elementPos.height, handle.y + handleHeight) - Math.min(elementPos.y, handle.y),
              false,
              'center',
              snap.isSameLevel,
              `element-${snap.elementID}-centerX-${snap.handleEdge}-${snap.elementEdge}-${index}`,
              [
                { x: centerX, y: elementPos.y },
                { x: centerX, y: elementPos.y + elementPos.height },
                { x: handle.x + handleWidth / 2, y: handle.y },
                { x: handle.x + handleWidth / 2, y: handle.y + handleHeight }
              ]
            ));
            break;
          }
          case 'centerY': {
            const centerY = elementPos.y + elementPos.height / 2;
            guides.push(createGuide(
              Math.min(elementPos.x, handle.x),
              centerY,
              Math.max(elementPos.x + elementPos.width, handle.x + handleWidth) - Math.min(elementPos.x, handle.x),
              1,
              false,
              'center',
              snap.isSameLevel,
              `element-${snap.elementID}-centerY-${snap.handleEdge}-${snap.elementEdge}-${index}`,
              [
                { x: elementPos.x, y: centerY },
                { x: elementPos.x + elementPos.width, y: centerY },
                { x: handle.x, y: handle.y + handleHeight / 2 },
                { x: handle.x + handleWidth, y: handle.y + handleHeight / 2 }
              ]
            ));
            break;
          }
          case 'left': {
            const x = snap.elementEdge === 'right' ? 
              elementPos.x + elementPos.width : 
              elementPos.x;
            const lineX = snap.handleEdge === snap.elementEdge ? 
              handle.x + handleWidth : 
              handle.x;
            guides.push(createGuide(
              x,
              Math.min(elementPos.y, handle.y),
              1,
              Math.max(elementPos.y + elementPos.height, handle.y + handleHeight) - Math.min(elementPos.y, handle.y),
              false,
              'edge',
              snap.isSameLevel,
              `element-${snap.elementID}-${snap.handleEdge}-${snap.elementEdge}-${index}`,
              [
                { x, y: elementPos.y },
                { x, y: elementPos.y + elementPos.height },
                { x: lineX, y: handle.y },
                { x: lineX, y: handle.y + handleHeight }
              ]
            ));
            break;
          }
          case 'right': {
            const x = snap.elementEdge === 'right' ? 
              elementPos.x + elementPos.width : 
              elementPos.x;
            const lineX = snap.handleEdge === snap.elementEdge ? 
              handle.x + handleWidth : 
              handle.x;
            guides.push(createGuide(
              x,
              Math.min(elementPos.y, handle.y),
              1,
              Math.max(elementPos.y + elementPos.height, handle.y + handleHeight) - Math.min(elementPos.y, handle.y),
              false,
              'edge',
              snap.isSameLevel,
              `element-${snap.elementID}-${snap.handleEdge}-${snap.elementEdge}-${index}`,
              [
                { x, y: elementPos.y },
                { x, y: elementPos.y + elementPos.height },
                { x: lineX, y: handle.y },
                { x: lineX, y: handle.y + handleHeight }
              ]
            ));
            break;
          }
          case 'top': {
            const y = snap.elementEdge === 'bottom' ? 
              elementPos.y + elementPos.height : 
              elementPos.y;
            const lineY = snap.handleEdge === snap.elementEdge ? 
              handle.y + handleHeight : 
              handle.y;
            guides.push(createGuide(
              Math.min(elementPos.x, handle.x),
              y,
              Math.max(elementPos.x + elementPos.width, handle.x + handleWidth) - Math.min(elementPos.x, handle.x),
              1,
              false,
              'edge',
              snap.isSameLevel,
              `element-${snap.elementID}-${snap.handleEdge}-${snap.elementEdge}-${index}`,
              [
                { x: elementPos.x, y },
                { x: elementPos.x + elementPos.width, y },
                { x: handle.x, y: lineY },
                { x: handle.x + handleWidth, y: lineY }
              ]
            ));
            break;
          }
          case 'bottom': {
            const y = snap.elementEdge === 'bottom' ? 
              elementPos.y + elementPos.height : 
              elementPos.y;
            const lineY = snap.handleEdge === snap.elementEdge ? 
              handle.y + handleHeight : 
              handle.y;
            guides.push(createGuide(
              Math.min(elementPos.x, handle.x),
              y,
              Math.max(elementPos.x + elementPos.width, handle.x + handleWidth) - Math.min(elementPos.x, handle.x),
              1,
              false,
              'edge',
              snap.isSameLevel,
              `element-${snap.elementID}-${snap.handleEdge}-${snap.elementEdge}-${index}`,
              [
                { x: elementPos.x, y },
                { x: elementPos.x + elementPos.width, y },
                { x: handle.x, y: lineY },
                { x: handle.x + handleWidth, y: lineY }
              ]
            ));
            break;
          }
        }
      }
    });

    return guides;
  }, [
    UIData.elementHandle,
    UIData.snapToEdge,
    UIData.snappedEdges,
    UIData.sceneZoom,
    launcherWindowData
  ]);

  if (!guides) return null;

  return <Container>{guides}</Container>;
});

SnappingGuideLines.displayName = 'GuideLines';
